import { useState, useEffect } from 'react';
import type { TabType } from '../components/TaskDetailsContent';
import type { Task } from '@/types';

interface TaskDetailsStateOptions {
  /** Hide notes tab (task has no linked notes) */
  hideNotes?: boolean;
  /** Hide comments tab (comments visible in right panel) */
  hideComments?: boolean;
}

interface TaskDetailsState {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
}

export function useTaskDetailsState(
  taskId?: string,
  task?: Task,
  options?: TaskDetailsStateOptions
): TaskDetailsState {
  const [activeTab, setActiveTab] = useState<TabType>('general');

  // Reset active tab to 'general' when opening a different task
  useEffect(() => {
    setActiveTab('general');
  }, [taskId]);

  // Reset active tab if current tab is no longer available
  useEffect(() => {
    if (!task) return;
    const availableTabs = new Set<TabType>(['general', 'history']);
    if (!options?.hideComments) availableTabs.add('comments');
    if (!options?.hideNotes) availableTabs.add('notes');
    if (task.geometries?.length) availableTabs.add('mapRepresentation');
    if (task.requirements?.length) availableTabs.add('requirements');
    if (task.relatedTasks?.length) availableTabs.add('relatedTasks');
    if (task.attachments?.length) availableTabs.add('attachments');
    if (!availableTabs.has(activeTab)) {
      setActiveTab('general');
    }
  }, [task, activeTab, options?.hideComments, options?.hideNotes]);

  return {
    activeTab,
    setActiveTab,
  };
}
